// apps/frontend/src/services/cartService.ts
// 🛒 Service Panier Ultra-Premium Enterprise EXTENDED
// Compatible avec votre architecture ordersService existante
// Version: 3.0.0 | Sprint: 5B | Quality: Enterprise Premium + Vinted-like

import apiClient from './apiClient';
import type { SupportedLocale } from '@mode-app/shared';
import type {
  Cart,
  CartVendorGroup,
  CartResponse,
  CartSummary,
  AddToCartRequest,
  UpdateCartItemRequest,
  RemoveFromCartRequest,
  SelectShippingMethodRequest,
  // 🆕 NOUVELLES INTERFACES VINTED-LIKE
  SetDeliveryMethodRequest,
  SetServicePointRequest,
  SetHomeAddressRequest,
  RefreshShippingQuoteRequest,
  ServicePoint,
  ShippingQuote,
  DeliveryMethod,
  ServicePointsResponse,
} from '@mode-app/shared';

interface CartStorageData {
  cart: Cart;
  lastSyncAt: string;
  version: string;
  locale: SupportedLocale;
  // 🆕 NOUVEAU CACHE SHIPPING
  shippingCache?: {
    [vendorId: string]: {
      quote: ShippingQuote;
      servicePoints?: ServicePoint[];
      calculatedAt: string;
      expiresAt: string;
    };
  };
}

// 🆕 NOUVELLES INTERFACES POUR GÉOLOCALISATION
interface GetServicePointsParams {
  vendorId: string;
  locale: SupportedLocale;
  userLocation?: {
    lat: number;
    lng: number;
  };
  deliveryMethod?: DeliveryMethod;
  radius?: number;
  carriers?: string[];
  limit?: number;
}

// ============================================================================
// 🎯 CONFIGURATION SERVICE ÉTENDUE
// ============================================================================

interface CartServiceConfig {
  cacheTimeout: number;
  syncInterval: number;
  maxRetries: number;
  storageKey: string;
  apiEndpoint: string;
  // 🆕 NOUVELLES CONFIGS VINTED-LIKE
  shippingCacheTimeout: number; // Cache shipping séparé
  geolocationTimeout: number;
  defaultSearchRadius: number;
  maxServicePoints: number;
}

const DEFAULT_CONFIG: CartServiceConfig = {
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
  syncInterval: 30 * 1000, // 30 secondes
  maxRetries: 3,
  storageKey: 'glowp_cart_v2',
  apiEndpoint: '/api/v1/cart',
  // 🆕 NOUVELLES CONFIGS
  shippingCacheTimeout: 15 * 60 * 1000, // 15 minutes pour shipping
  geolocationTimeout: 5000, // 5 secondes max pour géolocalisation
  defaultSearchRadius: 10, // 10km par défaut
  maxServicePoints: 50,
};

// ============================================================================
// 🛒 SERVICE PRINCIPAL ÉTENDU
// ============================================================================

export class CartService {
  private config: CartServiceConfig;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private syncTimer?: NodeJS.Timeout;
  private isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  constructor(config: Partial<CartServiceConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Online/offline detection
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.isOnline = true;
        this.syncWithServer();
      });
      window.addEventListener('offline', () => {
        this.isOnline = false;
      });
    }

    // // Auto-sync périodique
    // this.startAutoSync()
  }

  // ============================================================================
  // 📦 MÉTHODES PRINCIPALES EXISTANTES (CONSERVÉES)
  // ============================================================================

  /**
   * 🛒 Récupérer le panier complet
   */
  async getCart(locale: SupportedLocale): Promise<CartResponse> {
    try {
      const cacheKey = `cart_${locale}`;
      const cached = this.getFromCache(cacheKey);

      if (cached && this.isOnline) {
        return cached;
      }

      // Essayer de récupérer depuis le serveur
      if (this.isOnline) {
        const response = await apiClient.get<Cart>(
          `${this.config.apiEndpoint}?locale=${locale}`
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Cart loaded successfully',
          };

          this.setCache(cacheKey, cartResponse);
          this.saveToLocalStorage(response.data, locale);
          return cartResponse;
        }
      }

      // Fallback sur localStorage
      const localCart = this.loadFromLocalStorage(locale);
      if (localCart) {
        return {
          cart: localCart,
          message: 'Cart loaded from local storage',
          warnings: ['Working offline - changes will sync when online'],
        };
      }

      // Panier vide
      return this.createEmptyCart(locale);
    } catch (error: any) {
      console.error('Cart service error:', error);

      // Fallback sur localStorage en cas d'erreur réseau
      const localCart = this.loadFromLocalStorage(locale);
      if (localCart) {
        return {
          cart: localCart,
          message: 'Cart loaded from local storage',
          warnings: ['Server temporarily unavailable'],
        };
      }

      throw new Error(`Failed to load cart: ${error.message}`);
    }
  }

  /**
   * ➕ Ajouter un article au panier
   */
  async addToCart(request: AddToCartRequest): Promise<CartResponse> {
    try {
      const startTime = this.now();

      if (this.isOnline) {
        const response = await apiClient.post<Cart>(
          `${this.config.apiEndpoint}/items`,
          {
            productId: request.productId,
            quantity: request.quantity || 1,
            locale: request.locale,
          }
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Item added to cart',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          // Analytics
          this.trackAction('ADD_ITEM', {
            productId: request.productId,
            quantity: request.quantity,
            responseTime: this.now() - startTime,
          });

          return cartResponse;
        }
      }

      // Mode offline - simulation locale
      return this.addToCartLocally(request);
    } catch (error: any) {
      console.error('Add to cart error:', error);

      // Fallback mode offline
      if (!this.isOnline) {
        return this.addToCartLocally(request);
      }

      throw new Error(`Failed to add item to cart: ${error.message}`);
    }
  }

  /**
   * 🔄 Mettre à jour un article du panier
   */
  async updateCartItem(request: UpdateCartItemRequest): Promise<CartResponse> {
    try {
      if (this.isOnline) {
        const response = await apiClient.put<Cart>(
          `${this.config.apiEndpoint}/${request.itemId}`,
          {
            quantity: request.quantity,
            locale: request.locale,
          }
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Cart item updated',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          return cartResponse;
        }
      }

      return this.updateCartItemLocally(request);
    } catch (error: any) {
      console.error('Update cart item error:', error);

      if (!this.isOnline) {
        return this.updateCartItemLocally(request);
      }

      throw new Error(`Failed to update cart item: ${error.message}`);
    }
  }

  /**
   * 🗑️ Supprimer un article du panier
   */
  async removeFromCart(request: RemoveFromCartRequest): Promise<CartResponse> {
    try {
      if (this.isOnline) {
        const response = await apiClient.delete<Cart>(
          `${this.config.apiEndpoint}/items/${request.itemId}?locale=${request.locale}`
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Item removed from cart',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          return cartResponse;
        }
      }

      return this.removeFromCartLocally(request);
    } catch (error: any) {
      console.error('Remove from cart error:', error);

      if (!this.isOnline) {
        return this.removeFromCartLocally(request);
      }

      throw new Error(`Failed to remove item from cart: ${error.message}`);
    }
  }

  /**
   * 🚚 Sélectionner une méthode de livraison (EXISTANTE)
   */
  async selectShippingMethod(
    request: SelectShippingMethodRequest
  ): Promise<CartResponse> {
    try {
      if (this.isOnline) {
        const response = await apiClient.patch<Cart>(
          `${this.config.apiEndpoint}/shipping`,
          {
            vendorId: request.vendorId,
            shippingMethodId: request.shippingMethodId,
            locale: request.locale,
          }
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Shipping method updated',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          return cartResponse;
        }
      }

      return this.selectShippingMethodLocally(request);
    } catch (error: any) {
      console.error('Select shipping method error:', error);

      if (!this.isOnline) {
        return this.selectShippingMethodLocally(request);
      }

      throw new Error(`Failed to select shipping method: ${error.message}`);
    }
  }

  /**
   * 🧹 Vider le panier
   */
  async clearCart(locale: SupportedLocale): Promise<CartResponse> {
    try {
      if (this.isOnline) {
        const response = await apiClient.delete<Cart>(
          `${this.config.apiEndpoint}?locale=${locale}`
        );

        if (response.success) {
          const emptyCart = await this.createEmptyCart(locale);
          this.clearCache(`cart_${locale}`);
          this.clearLocalStorage(locale);

          return emptyCart;
        }
      }

      // Mode offline
      const emptyCart = await this.createEmptyCart(locale);
      this.clearLocalStorage(locale);
      return emptyCart;
    } catch (error: any) {
      console.error('Clear cart error:', error);
      throw new Error(`Failed to clear cart: ${error.message}`);
    }
  }

  /**
   * 📊 Obtenir le résumé du panier
   */
  async getCartSummary(locale: SupportedLocale): Promise<CartSummary> {
    try {
      const cartResponse = await this.getCart(locale);
      const cart = cartResponse.cart;

      return {
        itemsCount: cart.itemsCount,
        uniqueVendorsCount: cart.uniqueVendorsCount,
        subtotalCents: cart.subtotalCents || 0,
        totalShippingCents: cart.totalShippingCents || 0,
        totalProtectionCents: cart.totalProtectionCents || 0,
        estimatedTotalCents: cart.estimatedTotalCents || 0,
        currency: cart.currency,
        hasUnavailableItems: this.hasUnavailableItems(cart),
        hasPriceChanges: this.hasPriceChanges(cart),
        // 🆕 NOUVELLES VALIDATIONS VINTED
        hasUnreadyVendors: this.hasUnreadyVendors(cart),
        readyVendorsCount: this.getReadyVendorsCount(cart),
      };
    } catch (error: any) {
      console.error('Get cart summary error:', error);

      // Return empty summary on error
      return {
        itemsCount: 0,
        uniqueVendorsCount: 0,
        subtotalCents: 0,
        totalShippingCents: 0,
        totalProtectionCents: 0,
        estimatedTotalCents: 0,
        currency: 'EUR',
        hasUnavailableItems: false,
        hasPriceChanges: false,
        hasUnreadyVendors: false,
        readyVendorsCount: 0,
      };
    }
  }

  // ============================================================================
  // 🆕 NOUVELLES MÉTHODES VINTED-LIKE
  // ============================================================================

  /**
   * 🚚 Définir la méthode de livraison pour un vendeur
   */
  async setDeliveryMethod(
    request: SetDeliveryMethodRequest
  ): Promise<CartResponse> {
    try {
      const startTime = this.now();

      if (this.isOnline) {
        const response = await apiClient.patch<Cart>(
          `${this.config.apiEndpoint}/delivery-method`,
          {
            vendorId: request.vendorId,
            method: request.method,
            locale: request.locale,
          }
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Delivery method updated',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          // Analytics
          this.trackAction('SET_DELIVERY_METHOD', {
            vendorId: request.vendorId,
            method: request.method,
            responseTime: this.now() - startTime,
          });

          return cartResponse;
        }
      }

      // Mode offline - simulation locale
      return this.setDeliveryMethodLocally(request);
    } catch (error: any) {
      console.error('Set delivery method error:', error);

      if (!this.isOnline) {
        return this.setDeliveryMethodLocally(request);
      }

      throw new Error(`Failed to set delivery method: ${error.message}`);
    }
  }

  /**
   * 📍 Sélectionner un point de service
   */
  async setServicePoint(
    request: SetServicePointRequest
  ): Promise<CartResponse> {
    try {
      const startTime = this.now();

      if (this.isOnline) {
        const response = await apiClient.patch<Cart>(
          `${this.config.apiEndpoint}/service-point`,
          {
            vendorId: request.vendorId,
            servicePoint: request.servicePoint,
            locale: request.locale,
          }
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Service point selected',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          // Analytics
          this.trackAction('SET_SERVICE_POINT', {
            vendorId: request.vendorId,
            servicePointId: request.servicePoint.id,
            carrier: request.servicePoint.carrier,
            responseTime: this.now() - startTime,
          });

          return cartResponse;
        }
      }

      // Mode offline - simulation locale
      return this.setServicePointLocally(request);
    } catch (error: any) {
      console.error('Set service point error:', error);

      if (!this.isOnline) {
        return this.setServicePointLocally(request);
      }

      throw new Error(`Failed to set service point: ${error.message}`);
    }
  }

  /**
   * 🏠 Définir l'adresse de domicile
   */
  async setHomeAddress(request: SetHomeAddressRequest): Promise<CartResponse> {
    try {
      const startTime = this.now();

      if (this.isOnline) {
        const response = await apiClient.patch<Cart>(
          `${this.config.apiEndpoint}/home-address`,
          {
            vendorId: request.vendorId,
            address: request.address,
            locale: request.locale,
          }
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Home address updated',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          // Analytics
          this.trackAction('SET_HOME_ADDRESS', {
            vendorId: request.vendorId,
            city: request.address.city,
            country: request.address.country,
            responseTime: this.now() - startTime,
          });

          return cartResponse;
        }
      }

      // Mode offline - simulation locale
      return this.setHomeAddressLocally(request);
    } catch (error: any) {
      console.error('Set home address error:', error);

      if (!this.isOnline) {
        return this.setHomeAddressLocally(request);
      }

      throw new Error(`Failed to set home address: ${error.message}`);
    }
  }

  /**
   * 💰 Actualiser le devis de livraison pour un vendeur
   */
  async refreshShippingQuote(
    request: RefreshShippingQuoteRequest
  ): Promise<CartResponse> {
    try {
      const startTime = this.now();

      if (this.isOnline) {
        const response = await apiClient.post<Cart>(
          `${this.config.apiEndpoint}/refresh-quote`,
          {
            vendorId: request.vendorId,
            locale: request.locale,
          }
        );

        if (response.success && response.data) {
          const cartResponse: CartResponse = {
            cart: response.data,
            message: 'Shipping quote refreshed',
          };

          this.setCache(`cart_${request.locale}`, cartResponse);
          this.saveToLocalStorage(response.data, request.locale);

          // Mettre à jour le cache shipping
          const vendor = response.data.itemsByVendor.find(
            v => v.vendorId === request.vendorId
          );
          if (vendor?.shippingQuote) {
            this.cacheShippingQuote(request.vendorId, vendor.shippingQuote);
          }

          // Analytics
          this.trackAction('REFRESH_SHIPPING_QUOTE', {
            vendorId: request.vendorId,
            responseTime: this.now() - startTime,
          });

          return cartResponse;
        }
      }

      // Mode offline - simulation locale
      return this.refreshShippingQuoteLocally(request);
    } catch (error: any) {
      console.error('Refresh shipping quote error:', error);

      if (!this.isOnline) {
        return this.refreshShippingQuoteLocally(request);
      }

      throw new Error(`Failed to refresh shipping quote: ${error.message}`);
    }
  }

  /**
   * 📍 Charger les points de service pour un vendeur
   */
  async getServicePoints(
    params: GetServicePointsParams
  ): Promise<ServicePoint[]> {
    try {
      const {
        vendorId,
        locale,
        userLocation,
        deliveryMethod = 'relay',
        radius = this.config.defaultSearchRadius,
        carriers = [],
        limit = this.config.maxServicePoints,
      } = params;

      // Vérifier le cache
      const cacheKey = `service_points_${vendorId}_${deliveryMethod}_${radius}`;
      const cached = this.getFromCache(
        cacheKey,
        this.config.shippingCacheTimeout
      );
      if (cached && this.isOnline) {
        return cached;
      }

      if (this.isOnline) {
        const queryParams = new URLSearchParams({
          vendorId,
          locale,
          deliveryMethod,
          radius: radius.toString(),
          limit: limit.toString(),
        });

        if (userLocation) {
          queryParams.append('lat', userLocation.lat.toString());
          queryParams.append('lng', userLocation.lng.toString());
        }

        if (carriers.length > 0) {
          queryParams.append('carriers', carriers.join(','));
        }

        const response = await apiClient.get<ServicePointsResponse>(
          `${this.config.apiEndpoint}/service-points?${queryParams.toString()}`
        );

        if (response.success && response.data) {
          const servicePoints = response.data.points;

          // Mettre en cache
          this.setCache(
            cacheKey,
            servicePoints,
            this.config.shippingCacheTimeout
          );

          // Analytics
          this.trackAction('LOAD_SERVICE_POINTS', {
            vendorId,
            deliveryMethod,
            pointsFound: servicePoints.length,
            hasGeolocation: !!userLocation,
          });

          return servicePoints;
        }
      }

      // Mode offline - retourner points mockés ou cache local
      return this.getServicePointsLocally(params);
    } catch (error: any) {
      console.error('Get service points error:', error);

      if (!this.isOnline) {
        return this.getServicePointsLocally(params);
      }

      throw new Error(`Failed to load service points: ${error.message}`);
    }
  }

  // ============================================================================
  // 🔧 MÉTHODES UTILITAIRES ÉTENDUES
  // ============================================================================

  private async createEmptyCart(
    locale: SupportedLocale
  ): Promise<CartResponse> {
    const emptyCart: Cart = {
      id: `temp_${Date.now()}`,
      userId: '',
      itemsByVendor: [],
      subtotalCents: 0,
      totalShippingCents: 0,
      totalProtectionCents: 0,
      estimatedTotalCents: 0,
      currency: 'EUR',
      locale,
      lastItemAddedAt: new Date().toISOString(),
      itemsCount: 0,
      uniqueVendorsCount: 0,
    };

    return {
      cart: emptyCart,
      message: 'Empty cart created',
    };
  }

  private hasUnavailableItems(cart: Cart): boolean {
    return cart.itemsByVendor?.some((vendor: any) =>
      vendor.items.some((item: any) => !item.productSnapshot?.isStillAvailable)
    );
  }

  private hasPriceChanges(cart: Cart): boolean {
    return cart.itemsByVendor?.some((vendor: any) =>
      vendor.items.some(
        (item: any) => item.priceAtAdd !== item.productSnapshot?.currentPrice
      )
    );
  }

  // 🆕 NOUVELLES VALIDATIONS VINTED
  private hasUnreadyVendors(cart: Cart): boolean {
    return cart.itemsByVendor?.some(vendor => !this.isVendorReady(vendor));
  }

  private getReadyVendorsCount(cart: Cart): number {
    return (
      cart.itemsByVendor?.filter(vendor => this.isVendorReady(vendor)).length ||
      0
    );
  }

  private isVendorReady(vendor: CartVendorGroup): boolean {
    if (!vendor.deliveryMethod) return false;

    if (
      ((vendor.deliveryMethod as any) === 'relay' ||
        (vendor.deliveryMethod as any) === 'locker') &&
      !vendor.selectedServicePoint
    ) {
      return false;
    }

    if ((vendor.deliveryMethod as any) === 'home' && !vendor.homeAddress) {
      return false;
    }

    if (!vendor.shippingQuote) return false;

    const hasUnavailableItems = vendor.items.some(
      item => !item.productSnapshot.isStillAvailable
    );
    if (hasUnavailableItems) return false;

    return true;
  }

  // 🆕 GESTION CACHE SHIPPING
  private cacheShippingQuote(vendorId: string, quote: ShippingQuote): void {
    const cacheKey = `shipping_quote_${vendorId}`;
    const expiresAt = new Date(
      Date.now() + this.config.shippingCacheTimeout
    ).toISOString();

    this.setCache(
      cacheKey,
      {
        quote,
        calculatedAt: new Date().toISOString(),
        expiresAt,
      },
      this.config.shippingCacheTimeout
    );
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  // ============================================================================
  // 💾 CACHE & STORAGE ÉTENDUS
  // ============================================================================

  private getFromCache(key: string, timeout?: number): any {
    const cached = this.cache.get(key);
    const cacheTimeout = timeout || this.config.cacheTimeout;

    if (cached && Date.now() - cached.timestamp < cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any, _timeout?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  private clearCache(key: string): void {
    this.cache.delete(key);
  }

  private saveToLocalStorage(cart: Cart, locale: SupportedLocale): void {
    if (typeof window === 'undefined') return;

    try {
      const storageData: CartStorageData = {
        cart,
        lastSyncAt: new Date().toISOString(),
        version: '3.0.0', // Updated version
        locale,
        // 🆕 CACHE SHIPPING
        shippingCache: this.extractShippingCacheForStorage(),
      };

      localStorage.setItem(
        `${this.config.storageKey}_${locale}`,
        JSON.stringify(storageData)
      );
    } catch (error) {
      console.warn('Failed to save cart to localStorage:', error);
    }
  }

  private loadFromLocalStorage(locale: SupportedLocale): Cart | null {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(
        `${this.config.storageKey}_${locale}`
      );
      if (!stored) return null;

      const storageData: CartStorageData = JSON.parse(stored);

      // Restaurer le cache shipping
      if (storageData.shippingCache) {
        this.restoreShippingCacheFromStorage(storageData.shippingCache);
      }

      return storageData.cart;
    } catch (error) {
      console.warn('Failed to load cart from localStorage:', error);
      return null;
    }
  }

  private clearLocalStorage(locale: SupportedLocale): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(`${this.config.storageKey}_${locale}`);
      // Nettoyer aussi le cache shipping
      this.clearShippingCache();
    } catch (error) {
      console.warn('Failed to clear cart from localStorage:', error);
    }
  }

  // 🆕 GESTION CACHE SHIPPING STORAGE
  private extractShippingCacheForStorage(): any {
    const shippingCache: any = {};

    for (const [key, value] of this.cache.entries()) {
      if (
        key.startsWith('shipping_quote_') ||
        key.startsWith('service_points_')
      ) {
        const vendorId = key
          .replace(/^(shipping_quote_|service_points_)/, '')
          .split('_')[0];
        if (!shippingCache[vendorId]) {
          shippingCache[vendorId] = {};
        }

        if (key.startsWith('shipping_quote_')) {
          shippingCache[vendorId].quote = value.data.quote;
          shippingCache[vendorId].calculatedAt = value.data.calculatedAt;
          shippingCache[vendorId].expiresAt = value.data.expiresAt;
        } else if (key.startsWith('service_points_')) {
          shippingCache[vendorId].servicePoints = value.data;
        }
      }
    }

    return shippingCache;
  }

  private restoreShippingCacheFromStorage(shippingCache: any): void {
    for (const [vendorId, cacheData] of Object.entries(shippingCache)) {
      const data = cacheData as any;

      if (data.quote) {
        const quoteKey = `shipping_quote_${vendorId}`;
        this.cache.set(quoteKey, {
          data: {
            quote: data.quote,
            calculatedAt: data.calculatedAt,
            expiresAt: data.expiresAt,
          },
          timestamp: new Date(data.calculatedAt).getTime(),
        });
      }

      if (data.servicePoints) {
        const pointsKey = `service_points_${vendorId}_relay_${this.config.defaultSearchRadius}`;
        this.cache.set(pointsKey, {
          data: data.servicePoints,
          timestamp: new Date(data.calculatedAt).getTime(),
        });
      }
    }
  }

  private clearShippingCache(): void {
    for (const key of this.cache.keys()) {
      if (
        key.startsWith('shipping_quote_') ||
        key.startsWith('service_points_')
      ) {
        this.cache.delete(key);
      }
    }
  }

  // ============================================================================
  // 🔄 SYNCHRONISATION
  // ============================================================================

  private async syncWithServer(): Promise<void> {
    // Implementation de la sync sera ajoutée plus tard
  }

  private stopAutoSync(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }
  }

  // ============================================================================
  // 📊 ANALYTICS & TRACKING ÉTENDUS
  // ============================================================================

  private trackAction(action: string, data: any): void {
    // Implementation analytics sera ajoutée plus tard
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', `cart_${action.toLowerCase()}`, {
        event_category: 'cart',
        event_label: action,
        ...data,
      });
    }
  }

  // ============================================================================
  // 🔧 MÉTHODES OFFLINE ÉTENDUES (SIMULATION)
  // ============================================================================

  private async addToCartLocally(
    request: AddToCartRequest
  ): Promise<CartResponse> {
    // Implementation simplifiée pour mode offline
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    // Ajouter logique locale ici
    return {
      cart,
      message: 'Item added locally (will sync when online)',
      warnings: ['Working offline'],
    };
  }

  private async updateCartItemLocally(
    request: UpdateCartItemRequest
  ): Promise<CartResponse> {
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    return {
      cart,
      message: 'Item updated locally (will sync when online)',
      warnings: ['Working offline'],
    };
  }

  private async removeFromCartLocally(
    request: RemoveFromCartRequest
  ): Promise<CartResponse> {
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    return {
      cart,
      message: 'Item removed locally (will sync when online)',
      warnings: ['Working offline'],
    };
  }

  private async selectShippingMethodLocally(
    request: SelectShippingMethodRequest
  ): Promise<CartResponse> {
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    return {
      cart,
      message: 'Shipping method selected locally (will sync when online)',
      warnings: ['Working offline'],
    };
  }

  // 🆕 NOUVELLES MÉTHODES OFFLINE VINTED-LIKE
  private async setDeliveryMethodLocally(
    request: SetDeliveryMethodRequest
  ): Promise<CartResponse> {
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    // Simulation locale - mettre à jour le vendeur
    const vendor = cart.itemsByVendor.find(
      v => v.vendorId === request.vendorId
    );
    if (vendor) {
      vendor.deliveryMethod = request.method as any;
      // Reset service point/address si changement de méthode
      if (request.method === 'home') {
        vendor.selectedServicePoint = undefined;
      } else {
        vendor.homeAddress = undefined;
      }
    }

    this.saveToLocalStorage(cart, request.locale);

    return {
      cart,
      message: 'Delivery method set locally (will sync when online)',
      warnings: ['Working offline'],
    };
  }

  private async setServicePointLocally(
    request: SetServicePointRequest
  ): Promise<CartResponse> {
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    // Simulation locale - mettre à jour le point de service
    const vendor = cart.itemsByVendor.find(
      v => v.vendorId === request.vendorId
    );
    if (vendor) {
      vendor.selectedServicePoint = request.servicePoint;
    }

    this.saveToLocalStorage(cart, request.locale);

    return {
      cart,
      message: 'Service point selected locally (will sync when online)',
      warnings: ['Working offline'],
    };
  }

  private async setHomeAddressLocally(
    request: SetHomeAddressRequest
  ): Promise<CartResponse> {
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    // Simulation locale - mettre à jour l'adresse
    const vendor = cart.itemsByVendor.find(
      v => v.vendorId === request.vendorId
    );
    if (vendor) {
      vendor.homeAddress = request.address;
    }

    this.saveToLocalStorage(cart, request.locale);

    return {
      cart,
      message: 'Home address set locally (will sync when online)',
      warnings: ['Working offline'],
    };
  }

  private async refreshShippingQuoteLocally(
    request: RefreshShippingQuoteRequest
  ): Promise<CartResponse> {
    const cart =
      this.loadFromLocalStorage(request.locale) ||
      (await this.createEmptyCart(request.locale)).cart;

    // Mode offline - impossible de calculer un vrai devis
    // L'utilisateur doit être connecté pour avoir des tarifs réels

    return {
      cart,
      message: 'Cannot refresh shipping quote while offline',
      warnings: ['Internet connection required for shipping calculations'],
    };
  }

  private async getServicePointsLocally(
    _params: GetServicePointsParams
  ): Promise<ServicePoint[]> {
    // Simulation locale - retourner des points mock
    const mockServicePoints: ServicePoint[] = [
      {
        id: 'mock_1',
        name: 'Tabac de la Place (Offline)',
        carrier: 'MONDIAL_RELAY',
        lat: 48.8566,
        lng: 2.3522,
        address: '123 Rue de Rivoli',
        city: 'Paris',
        postalCode: '75001',
        countryCode: 'FR',
        distanceKm: 0.5,
        openingHours: {
          mon: { open: '08:00', close: '19:00' },
          tue: { open: '08:00', close: '19:00' },
          wed: { open: '08:00', close: '19:00' },
          thu: { open: '08:00', close: '19:00' },
          fri: { open: '08:00', close: '19:00' },
          sat: { open: '09:00', close: '18:00' },
          sun: { open: '00:00', close: '00:00', closed: true },
        },
        services: ['pickup', 'dropoff'],
        capacity: 'medium',
      },
      {
        id: 'mock_2',
        name: 'Relais Colis Plus (Offline)',
        carrier: 'MONDIAL_RELAY',
        lat: 48.8606,
        lng: 2.3376,
        address: '45 Avenue des Champs-Élysées',
        city: 'Paris',
        postalCode: '75008',
        countryCode: 'FR',
        distanceKm: 1.2,
        openingHours: {
          mon: { open: '07:00', close: '20:00' },
          tue: { open: '07:00', close: '20:00' },
          wed: { open: '07:00', close: '20:00' },
          thu: { open: '07:00', close: '20:00' },
          fri: { open: '07:00', close: '20:00' },
          sat: { open: '08:00', close: '19:00' },
          sun: { open: '10:00', close: '17:00' },
        },
        services: ['pickup', 'dropoff', 'returns'],
        capacity: 'large',
      },
    ];

    return mockServicePoints;
  }

  // ============================================================================
  // 🧹 CLEANUP ÉTENDU
  // ============================================================================

  destroy(): void {
    this.stopAutoSync();
    this.cache.clear();

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', () => (this.isOnline = true));
      window.removeEventListener('offline', () => (this.isOnline = false));
    }
  }
}

// ============================================================================
// 📤 EXPORT SINGLETON
// ============================================================================

export const cartService = new CartService();
export default cartService;
